import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { formatApiErrorDetail, useAuth } from "@/AuthContext";

import { API } from "./api";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [mode, setMode] = useState("form"); // "form" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const switchTab = (next) => {
    setTab(next);
    setMode("form");
    setError("");
    setNotice("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setNotice(data.message || "Check your inbox — if that email is registered, a reset link is on its way.");
      } else if (tab === "signin") {
        await login(email, password);
        navigate("/");
      } else {
        await register(name, email, password);
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-[#0a1c46] border border-white/10 rounded-2xl p-8 shadow-2xl shadow-blue-900/50">
        {mode === "form" && (
          <div className="flex rounded-full bg-[#04102e] p-1 mb-8">
            <button
              data-testid="auth-tab-signin"
              onClick={() => switchTab("signin")}
              className={`flex-1 rounded-full py-2 text-sm font-bold transition ${tab === "signin" ? "bg-[#ffcd3c] text-[#0a1a3f]" : "text-blue-200/70 hover:text-white"}`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => switchTab("register")}
              className={`flex-1 rounded-full py-2 text-sm font-bold transition ${tab === "register" ? "bg-[#ffcd3c] text-[#0a1a3f]" : "text-blue-200/70 hover:text-white"}`}
            >
              Create Account
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <h1 data-testid="auth-forgot-title" className="text-xl font-bold text-white mb-6">
            Reset your password
          </h1>
        )}

        <form data-testid="auth-form" onSubmit={submit} className="space-y-4">
          {mode === "form" && tab === "register" && (
            <input
              data-testid="auth-name-input"
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg bg-[#04102e] border border-white/15 px-4 py-3 text-white placeholder-blue-200/40 focus:outline-none focus:border-[#ffcd3c]"
            />
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            required
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg bg-[#04102e] border border-white/15 px-4 py-3 text-white placeholder-blue-200/40 focus:outline-none focus:border-[#ffcd3c]"
          />
          {mode === "form" && (
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={8}
              placeholder={tab === "register" ? "Password (8+ characters)" : "Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg bg-[#04102e] border border-white/15 px-4 py-3 text-white placeholder-blue-200/40 focus:outline-none focus:border-[#ffcd3c]"
            />
          )}
          {error && (
            <div data-testid="auth-error" className="rounded-lg bg-red-500/15 border border-red-400/40 text-red-200 text-sm px-4 py-3">
              {error}
            </div>
          )}
          {notice && (
            <div data-testid="auth-notice" className="rounded-lg bg-[#ffcd3c]/10 border border-[#ffcd3c]/40 text-[#ffe28a] text-sm px-4 py-3">
              {notice}
            </div>
          )}
          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[#ffcd3c] text-[#0a1a3f] font-bold py-3 hover:brightness-110 transition disabled:opacity-50"
          >
            {busy
              ? "Please wait…"
              : mode === "forgot"
                ? "Email Me a Reset Link"
                : tab === "signin"
                  ? "Sign In"
                  : "Create Account"}
          </button>
        </form>

        {mode === "form" && (
          <div className="mt-5 text-center">
            {tab === "signin" ? (
              <button
                data-testid="auth-forgot-link"
                onClick={() => {
                  setMode("forgot");
                  setError("");
                  setNotice("");
                }}
                className="text-sm text-blue-200/70 hover:text-[#ffcd3c] transition"
              >
                Forgot password?
              </button>
            ) : (
              <button
                data-testid="auth-back-to-signin"
                onClick={() => switchTab("signin")}
                className="text-sm text-blue-200/70 hover:text-[#ffcd3c] transition"
              >
                Already have an account? Sign In
              </button>
            )}
          </div>
        )}
        {mode === "forgot" && (
          <div className="mt-5 text-center">
            <button
              data-testid="auth-forgot-back"
              onClick={() => {
                setMode("form");
                setError("");
                setNotice("");
              }}
              className="text-sm text-blue-200/70 hover:text-[#ffcd3c] transition"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
